import React, { useCallback, useMemo } from 'react';
import type { ExtendedComponentSchema } from 'formiojs';
import { Components } from 'formiojs';
import type {
  EhsFormioReactProps,
  FormioBuilderInfo,
  FormioBuilderSchema
} from '../';
import { EhsFormioReactComponent } from '../';
import type { Component } from '@formio/react';
import { Select, TextInput } from '../../../form';
import type { LoadingStore } from '../../../../lib';
import { intl } from '../../../../lib';
import type { infrastructurePortalUtil } from 'generated-ts-common';
import type { LanguageProperty } from '../../../generated/LanguageProperty';
import { Maskable } from '../../../basic';
import { HBox } from '../../../layout';
import type { UseStore } from 'zustand';
import { useQuestionnaireValidation } from '../../questionnaire/useQuestionnaireValidation';
import { useLoadSVSSelectStoreData } from './UseLoadSVSSelectStoreData';
import {
  createOtherOption,
  getOtherOptionText,
  hasOtherOption,
  isGenCodeArray,
  otherValueIndicator,
  updateOtherOptionValue
} from './FormioSVSUtil';

type GenCode = infrastructurePortalUtil.saf.infrastructure.model.svs.GenCode;

/**
 * A store for retrieving and storing valuesets for the EhsFormioSVSSelect component.
 *
 */
export type EhsFormioSVSSelectStore = Omit<
  LoadingStore<string>,
  'wasLoaded' | 'loading'
> & {
  /** data expected to be a Map of valueSetOid to list of retrieved codes */
  data: Record<string, Array<GenCode>>;
  /** If data was loaded for the given valueSetOid. */
  wasLoaded: (valueSetOid: string) => boolean;
  /** loading state, which stores whether a specific valueSetOid is currently loading or not. */
  loading: Record<string, boolean>;
  /** Whether the given valueSetOid is currently loading. */
  isLoading: (valueSetOid: string) => boolean;
};

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
export type EhsFormioSVSSelectConstructor = {
  valueSetOid: string;
  includeOther: boolean;
  otherLabel: string;
  multiple?: boolean;
};
export type EhsFormioSVSSelectSettings = {
  valueSetOid: string;
  includeOther: boolean;
  otherLabel: string;
  store?: UseStore<EhsFormioSVSSelectStore>;
  multiple: boolean;
};
export const EhsFormioSVSSelectType = 'ehssvsselect';

export class EhsFormioSVSSelect extends EhsFormioReactComponent<
  EhsFormioSVSSelectSettings,
  EhsFormioSVSSelectConstructor
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: EhsFormioSVSSelectType,
      key: EhsFormioSVSSelectType,
      ...extend
    });
  }

  /**
   * defines how the component will look in the Form Editor (Builder)
   * https://help.form.io/developers/custom-components#component-methods
   */
  static get builderInfo(): FormioBuilderInfo {
    return {
      title: 'Ehs-SVSSelect',
      group: 'advanced',
      icon: 'file',
      documentation: '/developers/custom-components',
      weight: 40,
      schema: EhsFormioSVSSelect.schema()
    };
  }

  /**
   * defines the fields available in the Editor/Builder
   * https://help.form.io/developers/form-builder#overriding-behavior
   */
  static editForm() {
    return {
      components: [
        {
          type: 'textfield',
          key: 'label',
          label: 'label',
          tooltip: 'Please add only valid translation keys'
        },
        {
          type: 'textfield',
          key: 'valueSetOid',
          label: 'valueSetOid'
        },
        {
          type: 'checkbox',
          input: true,
          key: 'includeOther',
          label: 'Include Other Option',
          tooltip:
            'Enabling this will add an Other option to the field which, when selected, opens a textfield to enter the value.',
          weight: 30,
          defaultValue: true
        },
        {
          type: 'textfield',
          key: 'otherLabel',
          label: 'other field label',
          tooltip: 'Please add only valid translation keys',
          defaultValue: 'common.value'
        }
      ]
    };
  }

  private includeOther = true;
  private valueSetOid = '';
  private otherLabel = 'common.value';
  private multiple = false;

  //Begin Non static definitions-------------------------------------
  extractFields(component: Component<EhsFormioSVSSelectConstructor>): void {
    this.valueSetOid = component.valueSetOid;
    this.includeOther = component.includeOther;
    this.otherLabel = component.otherLabel;
    this.shouldSetValue = true;
    this.dataForSetting = this._data[component.key as string];
    this.multiple = component.multiple ?? false;
  }

  static store?: UseStore<EhsFormioSVSSelectStore>;

  attachEhsReact(): [
    (props: EhsFormioReactProps<EhsFormioSVSSelectSettings>) => JSX.Element,
    EhsFormioSVSSelectSettings
  ] {
    return [
      SVSSelectCmp,
      {
        valueSetOid: this.valueSetOid,
        includeOther: this.includeOther,
        otherLabel: this.otherLabel,
        store: EhsFormioSVSSelect.store,
        multiple: this.multiple
      }
    ];
  }
}

//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------

/**
 * The Logic for the Formio-Component.
 *
 * @param props formio {@link EhsFormioSVSSelectSettings} properties
 */
function SVSSelectCmp(
  props: Readonly<EhsFormioReactProps<EhsFormioSVSSelectSettings>>
): JSX.Element {
  const onChange = useCallback(
    (value: GenCode | GenCode[] | null): void => {
      if (isGenCodeArray(value)) {
        // we are casting it as string since we only allow that in  EhsFormioReactProps
        props.onChange((value as unknown) as string);
      } else {
        props.onChange(value?.valueMappedAsHL7 ?? '');
      }
    },
    [props]
  );

  if (props.settings.multiple) {
    return <SVSMultipleSelect {...props} onChange={onChange} />;
  }
  return <SVSSingleSelect {...props} onChange={onChange} />;
}

type SelectCmpProps = Pick<
  EhsFormioReactProps<EhsFormioSVSSelectSettings>,
  | 'settings'
  | 'label'
  | 'defaultValue'
  | 'keyForComponent'
  | 'readOnly'
  | 'required'
>;
type SingleSelectProps = SelectCmpProps & {
  onChange: (value: GenCode | null) => void;
  value?: string | null;
};
type MultipleSelectProps = SelectCmpProps & {
  onChange: (value: GenCode[] | null) => void;
  value?: string | string[] | GenCode[] | null;
};

function SVSSingleSelect({
  label,
  value,
  onChange,
  readOnly,
  required,
  defaultValue,
  settings,
  keyForComponent
}: Readonly<SingleSelectProps>) {
  const { errorMessage, isQuestionnaire } = useQuestionnaireValidation(
    keyForComponent
  );

  const { selectItems, items, isLoading } = useLoadSVSSelectStoreData({
    valueSetOid: settings.valueSetOid,
    includeOther: settings.includeOther,
    store: settings.store
  });

  // wrapped value which will either find the correct GenCode by comparing the valueMappedAsHL7 or use the other value if the value starts with the correct string
  const wrapperValue = useMemo(() => {
    // For clearing the value
    if (!value) {
      return null;
    }

    return settings.includeOther && value?.startsWith(otherValueIndicator)
      ? createOtherOption()
      : items.find(
          item =>
            item.valueMappedAsHL7 === value ||
            item.valueMappedAsHL7 === defaultValue
        ) ?? null;
  }, [value, settings.includeOther, items, defaultValue]);

  return useMemo(() => {
    return (
      <Maskable
        className="ehs-formio-svs-select"
        masked={isLoading(settings.valueSetOid)}
      >
        <HBox>
          <Select
            error={errorMessage}
            className="ehs-formio-svs-select-select"
            value={wrapperValue}
            onChange={onChange}
            label={label}
            itemValueProp="valueMappedAsHL7"
            itemLabelProp="displayName"
            variant="single"
            items={selectItems}
            readOnly={readOnly}
            required={required}
            labelOnTop={isQuestionnaire}
          />
          {settings.includeOther && value?.startsWith(otherValueIndicator) && (
            <TextInput
              type="text"
              label={intl(settings.otherLabel as LanguageProperty)}
              value={value.substring(otherValueIndicator.length)}
              onChange={newValue => {
                // the thing that's saved as the value is "OTHER___<value in the text field>"
                onChange({
                  ...createOtherOption(),
                  valueMappedAsHL7: otherValueIndicator + newValue
                });
              }}
              readOnly={readOnly}
            />
          )}
        </HBox>
      </Maskable>
    );
  }, [
    isLoading,
    settings.valueSetOid,
    settings.includeOther,
    settings.otherLabel,
    errorMessage,
    wrapperValue,
    onChange,
    label,
    selectItems,
    readOnly,
    required,
    isQuestionnaire,
    value
  ]);
}

function SVSMultipleSelect({
  label,
  value,
  onChange,
  readOnly,
  required,
  defaultValue,
  settings,
  keyForComponent
}: Readonly<MultipleSelectProps>) {
  const { errorMessage, isQuestionnaire } = useQuestionnaireValidation(
    keyForComponent
  );

  const { items, selectItems, isLoading } = useLoadSVSSelectStoreData({
    valueSetOid: settings.valueSetOid,
    store: settings.store,
    includeOther: settings.includeOther
  });

  const wrapperValue: GenCode[] = useMemo(() => {
    // For clearing the value, the value can be null or an empty array or empty string
    if (!value || value.length === 0) {
      return [];
    }

    if (isGenCodeArray(value)) {
      return value;
    }

    // in here value is string array and that only happens when there's some default value
    // only in readonly mode, so we are loading the default values
    return (
      defaultValue.map((val: string) =>
        items.find(item => item.valueMappedAsHL7 === val)
      ) ?? []
    );
  }, [items, value, defaultValue]);

  return useMemo(() => {
    return (
      <Maskable
        className="ehs-formio-svs-select"
        masked={isLoading(settings.valueSetOid)}
      >
        <HBox>
          <Select
            error={errorMessage}
            className="ehs-formio-svs-select-select"
            value={wrapperValue}
            onChange={onChange}
            label={label}
            itemValueProp="valueMappedAsHL7"
            itemLabelProp="displayName"
            variant="multi"
            items={selectItems}
            readOnly={readOnly}
            required={required}
            labelOnTop={isQuestionnaire}
          />
          {settings.includeOther &&
            isGenCodeArray(value) &&
            hasOtherOption(value) && (
              <TextInput
                type="text"
                label={intl(settings.otherLabel as LanguageProperty)}
                value={getOtherOptionText(value)}
                onChange={newValue => {
                  // the thing that's saved as the value is "OTHER___<value in the text field>" in correct genCode
                  onChange(updateOtherOptionValue(value, newValue));
                }}
                readOnly={readOnly}
              />
            )}
        </HBox>
      </Maskable>
    );
  }, [
    isLoading,
    settings.valueSetOid,
    settings.includeOther,
    settings.otherLabel,
    errorMessage,
    wrapperValue,
    onChange,
    label,
    selectItems,
    readOnly,
    required,
    isQuestionnaire,
    value
  ]);
}
